import { useCallback, useEffect, useRef, useState } from 'react'
import { ArrowUpRight, CalendarDays, MoreHorizontal, UserPlus } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useRouterState } from '@tanstack/react-router'
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine'
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { getDueDateColorClass } from '../utils/datetime'
import { TaskRowAssigneePicker, TaskRowDatePicker } from './TaskActionMenus'
import { formatTaskDueDate } from './TaskList'
import type { SubTaskItemProps } from '../type'
import { cn } from '@/lib/utils'
import { TaskStatusCheckbox } from '@/components/app/tasks/TaskStatusCheckbox'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useTaskMutations } from '@/lib/db/rxdb/hooks'
import { createDragExtractor } from '@/lib/utils/dragDrop'


export type SubTaskReorderEvent = {
  sourceId: string
  targetId: string
  dropPosition: 'before' | 'after'
}

const extractSubTaskDragData = createDragExtractor('sub-task')

export function SubTaskItem({
  subTask,
  draggingId,
  onDragStart,
  onDragEnd,
  onReorder,
  dropIndicator,
  onDropIndicatorChange,
  onSubTaskClick,
  statusMode,
  identifier,
  assignee,
  showAssign = true,
}: SubTaskItemProps) {
  const { t } = useTranslation('tasks')
  const itemRef = useRef<HTMLDivElement>(null)
  const [openMenu, setOpenMenu] = useState<'date' | 'assign' | 'more' | null>(null)
  const navigate = useNavigate()
  const params = useParams({ strict: false })
  const routerState = useRouterState()
  const workspaceSlug = params.workspaceSlug
  const { updateTask, deleteTask } = useTaskMutations()

  const handleStatusChange = useCallback(
    async (statusId: string) => {
      const currentStatusId = subTask.statusId || null
      if (currentStatusId === statusId) {
        return
      }

      try {
        await updateTask(subTask.id, { statusId })
      } catch (error) {
        toast.error('Failed to update task status')
      }
    },
    [subTask.id, subTask.statusId, updateTask],
  )

  // Determine the source based on current route
  const getSourceFromRoute = ():
    | 'tasks'
    | 'today'
    | 'upcoming'
    | 'someday' => {
    const pathname = routerState.location.pathname
    if (pathname.includes('/tasks/today')) return 'today'
    if (pathname.includes('/tasks/upcoming')) return 'upcoming'
    if (pathname.includes('/tasks/someday')) return 'someday'
    return 'tasks'
  }

  // Set up drag and drop
  useEffect(() => {
    const element = itemRef.current
    if (!element) {
      return
    }

    if (!onReorder) {
      return
    }

    return combine(
      // The whole row drags; there is no handle until you do (design 28b / 23).
      draggable({
        element,
        getInitialData: () => ({ type: 'sub-task', id: subTask.id }),
        onDragStart: () => {
          onDragStart?.(subTask.id)
          onDropIndicatorChange?.(null)
        },
        onDrop: () => {
          onDragEnd?.()
          onDropIndicatorChange?.(null)
        },
      }),
      dropTargetForElements({
        element,
        canDrop: ({ source }) => Boolean(extractSubTaskDragData(source.data)),
        getData: () => ({ type: 'sub-task', id: subTask.id }),
        onDropTargetChange: ({ location, source, self }) => {
          const itemData = extractSubTaskDragData(source.data)
          if (!itemData || itemData.id === subTask.id) {
            if (onDropIndicatorChange && dropIndicator?.id === subTask.id) {
              onDropIndicatorChange(null)
            }
            return
          }

          const rect = (self.element as HTMLElement).getBoundingClientRect()
          const pointerY = location.current.input.clientY
          const shouldPlaceAfter = pointerY > rect.top + rect.height / 2

          onDropIndicatorChange?.({
            id: subTask.id,
            position: shouldPlaceAfter ? 'after' : 'before',
          })
        },
        onDragLeave: () => {
          if (onDropIndicatorChange && dropIndicator?.id === subTask.id) {
            onDropIndicatorChange(null)
          }
        },
        onDrop: ({ location, source, self }) => {
          const itemData = extractSubTaskDragData(source.data)
          onDropIndicatorChange?.(null)
          if (!itemData || itemData.id === subTask.id) {
            return
          }

          const innermostTarget = location.current.dropTargets[0]
          if (innermostTarget?.element !== self.element) {
            return
          }

          const rect = (self.element as HTMLElement).getBoundingClientRect()
          const pointerY = location.current.input.clientY
          const shouldPlaceAfter = pointerY > rect.top + rect.height / 2

          onReorder({
            sourceId: itemData.id,
            targetId: subTask.id,
            dropPosition: shouldPlaceAfter ? 'after' : 'before',
          })
        },
      }),
    )
  }, [
    subTask.id,
    onReorder,
    onDragStart,
    onDragEnd,
    onDropIndicatorChange,
    dropIndicator?.id,
  ])

  const isDragging = draggingId === subTask.id
  const showIndicatorBefore =
    dropIndicator?.id === subTask.id && dropIndicator?.position === 'before'
  const showIndicatorAfter =
    dropIndicator?.id === subTask.id && dropIndicator?.position === 'after'

  const handleSubTaskClick = () => {
    // If a callback is provided, use it instead of navigation
    if (onSubTaskClick) {
      onSubTaskClick(subTask.id)
      return
    }

    // Otherwise, fall back to navigation
    if (!workspaceSlug) {
      return
    }

    const source = getSourceFromRoute()

    const navState: Record<string, unknown> = { from: source }

    navigate({
      to: '/$workspaceSlug/tasks/$taskId',
      params: { workspaceSlug, taskId: subTask.id },
      state: navState,
    })
  }

  const openFullPage = () => {
    if (!workspaceSlug) return
    void navigate({ to: '/$workspaceSlug/tasks/$taskId', params: { workspaceSlug, taskId: subTask.id } })
  }

  const menuProps = (menu: 'date' | 'assign' | 'more') => ({
    open: openMenu === menu,
    onOpenChange: (open: boolean) => setOpenMenu(open ? menu : null),
  })
  const trigger =
    'flex size-[26px] items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground data-[state=open]:bg-secondary data-[state=open]:text-foreground'
  const apply = (updates: Parameters<typeof updateTask>[1]) => void updateTask(subTask.id, updates)

  return (
    <div
      ref={itemRef}
      data-testid="subtask-item"
      onClick={handleSubTaskClick}
      className={cn(
        'group relative flex h-9 cursor-pointer items-center gap-2.5 rounded-md px-1.5 transition-colors [--row-bg:var(--background)] hover:bg-secondary hover:[--row-bg:var(--secondary)] active:cursor-grabbing',
        isDragging && 'opacity-40',
      )}
    >
      {showIndicatorBefore && (
        <div className="pointer-events-none absolute inset-x-0 top-0 h-0.5 rounded bg-brand" />
      )}
      <div
        className="flex size-5 shrink-0 items-center justify-center"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <TaskStatusCheckbox
          currentStatusId={subTask.statusId}
          onStatusChange={handleStatusChange}
          statusMode={statusMode}
          className="h-[18px] w-[18px]"
        />
      </div>
      {identifier && (
        <span className="shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground">{identifier}</span>
      )}
      <span data-testid="subtask-name" className="min-w-0 flex-1 truncate text-left text-[13.5px] text-foreground">
        {subTask.name || t('subTask.untitled')}
      </span>
      {assignee && (
        <Avatar className="size-5 shrink-0" title={assignee.name}>
          <AvatarImage src={assignee.src} alt={assignee.name} />
          <AvatarFallback className="text-[9px]">{assignee.initials}</AvatarFallback>
        </Avatar>
      )}
      {subTask.dueDate != null && (
        <span className={cn('w-12 shrink-0 text-right text-xs', getDueDateColorClass(subTask.dueDate))}>
          {formatTaskDueDate(subTask.dueDate)}
        </span>
      )}

      {/* Actions float over the row's right end on hover, covering the date (design 23 / 28b). */}
      <div
        className={cn(
          'pointer-events-none absolute inset-y-0 right-0 flex items-center gap-0.5 bg-gradient-to-l from-[var(--row-bg)] from-60% to-transparent pl-10 pr-1.5 opacity-0 transition-opacity',
          'group-hover:pointer-events-auto group-hover:opacity-100 focus-within:pointer-events-auto focus-within:opacity-100',
          (openMenu || isDragging) && 'pointer-events-auto opacity-100',
        )}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {showAssign && (
          <TaskRowAssigneePicker
            task={subTask}
            onApply={apply}
            {...menuProps('assign')}
            triggerClassName={trigger}
            icon={<UserPlus className="size-3.5" />}
            label={t('bulk.assign')}
          />
        )}
        <TaskRowDatePicker
          task={subTask}
          {...menuProps('date')}
          trigger={
            <button
              type="button"
              className={trigger}
              aria-label={t('bulk.schedule')}
              title={t('bulk.schedule')}
              onClick={() => setOpenMenu('date')}
            >
              <CalendarDays className="size-3.5" />
            </button>
          }
        />
        <button
          type="button"
          className={trigger}
          aria-label={t('detailHeader.expand')}
          title={t('detailHeader.expand')}
          onClick={openFullPage}
        >
          <ArrowUpRight className="size-3.5" />
        </button>
        <DropdownMenu {...menuProps('more')}>
          <DropdownMenuTrigger className={trigger} aria-label={t('rowKeys.more')} title={t('rowKeys.more')}>
            <MoreHorizontal className="size-3.5" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onSelect={openFullPage}>{t('detailHeader.expand')}</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive"
              onSelect={() => {
                void deleteTask(subTask.id).then(() => {
                  toast(t('rowKeys.deleted'), {
                    action: {
                      label: t('rowKeys.undo'),
                      onClick: () => void updateTask(subTask.id, { isDeleted: false, deletedOn: null, deletedBy: null }),
                    },
                  })
                })
              }}
            >
              {t('rowKeys.delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {showIndicatorAfter && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-0.5 rounded bg-brand" />
      )}
    </div>
  )
}
